import { useMemo } from "react";

import type { OrderRow } from "../../../types";
import type { ProcessedHistoryData, ProcessedHistoryOrder } from "./types";
import { daysBetween, parseOrderDate } from "./utils";

export function useProcessedHistoryOrders(
    orders: OrderRow[],
    contextualCustomerName: string,
    contextualBusinessKey: string
): ProcessedHistoryData {
    return useMemo(() => {
        const processedOrders: ProcessedHistoryOrder[] = orders
            .map((order) => ({
                ...order,
                parsedDate: parseOrderDate(order.createdAt),
                parsedTime: parseOrderDate(order.createdAt)?.getTime() ?? NaN,
            }))
            .filter((order): order is ProcessedHistoryOrder =>
                order.parsedDate !== null && !isNaN(order.parsedTime)
            )
            .sort((a, b) => a.parsedTime - b.parsedTime);

        if (processedOrders.length === 0) {
            return {
                orders: [],
                firstOrder: null,
                latestOrder: null,
                totalValue: 0,
                intervals: [],
                averageInterval: null,
                averageMonthlyFrequency: 0,
                yearlyStats: [],
            };
        }

        const firstOrder = processedOrders[0];
        const latestOrder = processedOrders[processedOrders.length - 1];
        const totalValue = processedOrders.reduce((sum, order) => sum + order.value, 0);
        const intervals = processedOrders.map((order, index) => ({
            order,
            intervalDays: index === 0 ? null : daysBetween(processedOrders[index - 1].parsedDate, order.parsedDate),
        }));

        const intervalValues = intervals
            .map((item) => item.intervalDays)
            .filter((days): days is number => days !== null && isFinite(days));

        const averageInterval = intervalValues.length > 0
            ? intervalValues.reduce((sum, value) => sum + value, 0) / intervalValues.length
            : null;

        const yearlyMap = new Map<number, { orders: number; totalValue: number }>();

        for (const order of processedOrders) {
            const year = order.parsedDate.getFullYear();
            if (!isFinite(year)) continue;

            const stats = yearlyMap.get(year) || { orders: 0, totalValue: 0 };
            stats.orders += 1;
            stats.totalValue += order.value;
            yearlyMap.set(year, stats);
        }

        const yearlyStats = Array.from(yearlyMap.entries())
            .map(([year, { orders, totalValue }]) => ({
                year,
                orders,
                totalValue,
                averageValue: orders > 0 ? totalValue / orders : 0,
            }))
            .sort((a, b) => b.year - a.year);

        return {
            orders: processedOrders,
            firstOrder,
            latestOrder,
            totalValue,
            intervals,
            averageInterval,
            averageMonthlyFrequency: processedOrders.length / 12,
            yearlyStats,
        };
    }, [orders, contextualBusinessKey, contextualCustomerName]);
}
